import { property, customElement } from 'lit/decorators.js';
import { Subscription } from 'rxjs';
import {
  ApiService,
  ProductListRequestApiService,
} from '@trp-dms-digital-shared/ds-ui-library-api';
import { PopBaseComponent } from './pop-base-component';
import { Constants } from './base.constants';

/**
 * Base class for components that fetch data from the DADA API.
 */
@customElement('pop-api-base-component')
export class PopApiBaseComponent extends PopBaseComponent {
  /**
   * @internal
   */
  apiService: ApiService | ProductListRequestApiService;

  /**
   * @internal
   */
  apiServiceFetchSubscription!: Subscription;

  /**
   * The fragment used to fetch the data for the component.
   */
  @property({
    attribute: false,
  })
  protected fragment!: string;

  /**
   * Product code
   */
  @property({
    attribute: Constants.DATA_PRODUCT_CODE,
    type: String,
  })
  protected productCode?: string;

  /**
   * Vehicle
   */
  @property({
    attribute: Constants.DATA_VEHICLE,
    type: String,
  })
  protected vehicles?: string[];

  /**
   * Audience for product
   */
  @property({
    attribute: Constants.DATA_AUDIENCE,
    type: String,
  })
  protected audience?: string;

  constructor(apiService: ApiService | ProductListRequestApiService) {
    super();

    this.apiService = apiService;
  }

  connectedCallback() {
    super.connectedCallback();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected processResponse(_response) {
    // Component must override
    console.warn('processResponse must be overridden');
  }

  /**
   * Initializes the component by fetching data from the API and setting up the component object.
   * If the component object is already initialized, this method does nothing.
   */
  protected initialize() {
    if (this.componentObject) {
      super.initialize();
      return;
    }

    let requiredProps = '';
    if (!this.productCode && !this.vehicles) {
      requiredProps += 'product-code or vehicle required\n';
    }
    if (!this.audience) {
      requiredProps += 'audience required\n';
    }
    if (!this.country) {
      requiredProps += 'country required\n';
    }
    if (!this.language) {
      requiredProps += 'language required\n';
    }
    if (!this.fragment) {
      requiredProps += 'fragment required\n';
    }
    if (requiredProps !== '') {
      console.error(requiredProps);
      return;
    }

    this.setupApiServiceSubscription('initialized');
    this.callApiService();
    super.initialize();
  }

  private setupApiServiceSubscription(eventName: string) {
    if (
      this.popConfiguration.getConfiguration(Constants.DADA_ENDPOINT) !==
      undefined
    ) {
      this.apiService?.setEndpointAndApikey(
        this.popConfiguration.getConfiguration(Constants.DADA_ENDPOINT),
        this.popConfiguration.getConfiguration(Constants.DADA_APIKEY),
      );
    }

    this.apiServiceFetchSubscription = this.apiService
      ?.fetch()
      .subscribe((response) => {
        // Emit event to notify that the component has been initialized
        const renderedEvent = new CustomEvent(
          `pop:product:${this.tagName}:${eventName}`,
          {
            detail: {
              data: response,
            },
          },
        );

        if (typeof window !== 'undefined') {
          window.dispatchEvent(renderedEvent);
        }

        this.processResponse(response);

        this.apiServiceFetchSubscription.unsubscribe();
      });
  }

  protected callApiService(requestObject = {}, eventName = 'refreshData') {
    this.setupApiServiceSubscription(eventName);
    const request = { ...this.getDefaultRequest(), ...requestObject };
    this.apiService?.updateRequest(request, this.fragment);
  }

  private getDefaultRequest() {
    return {
      productCode: this.productCode,
      vehicles: this.vehicles,
      context: {
        audience: this.audience,
        country: this.country,
        language: this.language,
      },
    };
  }
}
